import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import { MAX_COMPETITIONS_PAGE, type CompetitionRow, type CompetitionsList, type CompetitionsQuery } from '@/api/competitions'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'

import { nextOffset } from '../list/paging'
import { useServerList } from '../list/useServerList'

/* The list's requests: its pages, and the categories read for the filter. */

/** Keyed by the query less its page: `useInfiniteQuery` keeps the pages within one entry. */
export const competitionsKeys = {
  all: ['analytics-competitions'] as const,
  categories: (kind: ApiClientKind, filters: GlobalFilters) =>
    ['analytics-competitions', 'categories', kind, filtersKey(filters)] as const,
  list: (kind: ApiClientKind, query: CompetitionsQuery) =>
    [
      'analytics-competitions',
      kind,
      filtersKey(query.filters),
      query.statuses ?? [],
      query.categories ?? [],
      query.sort,
      query.order,
    ] as const,
}

/** A competition is the same competition on every page. */
const identify = (row: CompetitionRow) => row.competitionId

/** The list's pages, folded (`../list/useServerList`). */
export function useCompetitionsList(query: CompetitionsQuery) {
  const client = useApiClient()
  return useServerList<CompetitionRow, CompetitionsList>({
    queryKey: competitionsKeys.list(client.kind, query),
    fetchPage: (page) => client.competitions({ ...query, ...page }),
    identify,
  })
}

/**
 * Every category in the global scope, for the category filter. No route lists them, so
 * the list itself is read unfiltered by status or category, page by page at the API's
 * largest page (`nextOffset`), until every competition is seen: one request for a
 * tenant of up to 200. `complete` is false when fewer competitions came back than the
 * API counted (the list changed during the read), so the filter can say so.
 */
export function useCompetitionCategories(filters: GlobalFilters) {
  const client = useApiClient()
  return useQuery({
    queryKey: competitionsKeys.categories(client.kind, filters),
    queryFn: async () => {
      const categories = new Set<string>()
      const seen = new Set<string>()
      for (let offset: number | undefined = 0; ; ) {
        const { data } = await client.competitions({ filters, sort: 'name', order: 'asc', limit: MAX_COMPETITIONS_PAGE, offset })
        for (const row of data.rows) {
          seen.add(row.competitionId)
          if (row.category) categories.add(row.category)
        }
        offset = nextOffset(data, offset)
        if (offset === undefined) return { categories: [...categories], complete: seen.size >= data.total }
      }
    },
    placeholderData: keepPreviousData,
  })
}
